import { useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { OAuthErrorCard } from '../OAuthErrorCard'
import { runOAuthStep, sessionOrSignIn, signedOAuthQuery } from '../oauthFlow'
import type { OAuthFlowError } from '../oauthFlow'
import { continueOAuthAuthorization } from '@/lib/auth/authApi'

export const Route = createFileRoute('/_AuthLayout/oauth/login')({
  component: OAuthLoginPage,
})

/**
 * The MCP OAuth provider's login page. Signed out: sign in, then come back here.
 * Signed in: resume the authorization, which continues to consent (or the client).
 */
function OAuthLoginPage() {
  const { t } = useTranslation('auth')
  const [error, setError] = useState<OAuthFlowError | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const session = sessionOrSignIn()
    if (!session) return
    void runOAuthStep(() => continueOAuthAuthorization(session, signedOAuthQuery())).then((result) => {
      if (!result) return
      if ('url' in result) window.location.href = result.url
      else setError(result.error)
    })
  }, [])

  if (error) return <OAuthErrorCard error={error} />

  return (
    <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground" role="status" aria-live="polite">
      <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
      <p className="text-[15px]">{t('oauth.resuming')}</p>
    </div>
  )
}
